import { FC, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { HUBSPOT_FORM_IDS } from '@/lib/constants';
import { GlobalContext } from '@/pages/_app';

interface Props {
	formId: string;
	handleClose: () => void;
}

const HubspotForm: FC<Props> = ({ formId, handleClose }) => {
	const { isConsentGiven } = useContext(GlobalContext);

	const handleFormReady = () => {
		window.location.hash = 'form_' + formId;
	};

	const handleFormUnmount = () => {
		window.location.hash = '';
	};

	useEffect(() => {
		if (isConsentGiven) {
			const loadHubspotForm = () => {
				(window as any).hbspt?.forms?.create({
					formId,
					region: 'na1',
					target: '#hubspotFormWrapper',
					portalId: HUBSPOT_FORM_IDS.PORTAL,
					onFormReady: handleFormReady,
				});
			};

			if ((window as any)?.hbspt) {
				loadHubspotForm();

				return;
			}

			const script = document.createElement('script');

			script.src = 'https://js.hsforms.net/forms/v2.js';
			script.async = true;
			script.onload = loadHubspotForm;

			document.body.appendChild(script);

			return () => handleFormUnmount();
		}
		const initialLink =
			HUBSPOT_FORM_IDS.LINK + '/' + HUBSPOT_FORM_IDS.PORTAL + '/' + formId;

		window.open(initialLink, '_blank');
		handleClose();
	}, [isConsentGiven]);

	return <HubspotFormWrapper id="hubspotFormWrapper" />;
};

const HubspotFormWrapper = styled.div`
	.hs-input {
		background: #f5f4f9;
		border: 1px solid #7360f2;
		box-sizing: border-box;
		border-radius: 12px;
		padding: 14px 20px;
		width: 100% !important;
		padding-right: 30px;
		color: #7360f2;
		font-family: var(--font-sans);

		&:focus {
			outline: none;
		}
	}
	h1,
	h2,
	h1 span {
		font-family: var(--font-sans) !important;

		font-weight: var(--font-bold);
		font-size: 42px !important;
		line-height: 50px;
		color: #7360f2;
		text-align: center;

		@media (max-width: 600px) {
			font-size: 28px !important;
			line-height: 32px;
		}
	}

	img {
		display: none;
	}
	fieldset > .div {
		padding-right: 30px;
	}

	.hs-form-field {
		margin-bottom: 20px;

		@media (max-width: 600px) {
			margin-bottom: 15px;
		}
	}

	.hs-richtext,
	span {
		font-family: var(--font-sans);

		font-size: 16px;
		line-height: 25px;
		color: #776fa9;
	}

	.hs-richtext {
		margin-bottom: 2rem;
	}

	div.hs_email {
		display: flex;
		flex-direction: column;
	}

	.hs-field-desc {
		order: 3;
		margin-top: 9px;
		font-family: var(--font-sans);

		font-weight: var(--font-medium);
		font-size: 12px;
		line-height: 16px;
		color: #776fa9;
	}

	fieldset {
		margin: 0 auto !important;
		max-width: initial !important;
	}

	.hs-input::placeholder {
		color: transparent;
	}

	select.hs-input {
		color: #7360f2;
		font-family: var(--font-sans);

		font-weight: var(--font-medium);
		font-size: 14px;
		line-height: 20px;
		appearance: none;
	}

	.hs-error-msg {
		display: none;
	}
	fieldset:nth-of-type(8) {
		width: 100%;
	}

	.hs-form-booleancheckbox label {
		background: #ffffff;
		display: block;
		background: #ffffff;
		border-radius: 18px;
		padding: 15px 20px;
		display: flex;
		align-items: center;
		cursor: pointer;
	}

	.hs-input[type='checkbox'] {
		margin-top: -1px;
		width: auto !important;

		& + span {
			margin-left: 24px;
		}
	}

	.hs-input[type='radio'] {
		margin-top: -1px;
		width: auto !important;
		position: relative;

		& + span {
			margin-left: 24px;
		}
	}

	.hs-form-radio,
	.hs-form-checkbox {
		margin-top: 8px;
	}

	.legal-isConsentGiven-container .hs-form-booleancheckbox-display > span {
		margin-left: 13px;
	}

	.form-columns-2 .hs_firstname {
		width: calc(50% - 20px);
		float: left;

		@media (max-width: 600px) {
			width: 100% !important;
		}
	}

	.form-columns-2 .hs-lastname {
		width: calc(50% - 20px);
		float: right !important;

		@media (max-width: 600px) {
			width: 100% !important;
		}
	}

	.form-columns-2 .hs-form-field {
		@media (max-width: 600px) {
			width: 100% !important;
		}
	}

	.hs-richtext {
		> p {
			font-family: var(--font-sans);

			font-weight: var(--font-medium);
			font-size: 12px;
			line-height: 16px;
			color: #776fa9;
			margin-top: 10px;
			margin-bottom: 20px;
		}
	}

	.legal-isConsentGiven-container .field.hs-form-field {
		margin-bottom: 0;
	}

	.hs-main-font-element {
		font-family: var(--font-sans);

		font-weight: var(--font-medium);
		font-size: 12px;
		line-height: 16px;
		color: #f4447a;
	}

	.primary {
		z-index: 100;
		margin: 20px auto 0;
		background: linear-gradient(rgb(115, 96, 242), rgb(91, 73, 212) 100%);
		border-radius: 18px;
		width: 100%;
		padding: 20px 0;
		font-family: var(--font-sans);

		font-weight: var(--font-bold);
		font-size: 18px;
		line-height: 15px;
		text-transform: uppercase;
		color: rgb(255, 255, 255);
		cursor: pointer;
		outline: none;

		&:hover {
			background: linear-gradient(180deg, rgba(115, 96, 242) 0%, #9263f7 100%);
		}

		@media (max-width: 600px) {
			font-size: 14px;
			padding: 15px 0;
		}
	}

	.actions {
		&:after {
			content: '';
			z-index: 30;
			position: absolute;
			width: 45%;
			height: 30px;
			left: 50%;
			transform: translateX(-50%);
			background: #282253;
			opacity: 0.3;
			filter: blur(27px);
			border-radius: 20px;

			@media (max-width: 600px) {
				opacity: 0;
				display: none;
			}
		}
	}

	.hs_country_dropdown,
	.hs_top_markets,
	.hs_initial_traffic_estimation__dropdown_ {
		width: 100%;
	}

	.form-columns-2 .hs-input:nth-of-type(1) {
		width: calc(50% - 20px);
		float: left;

		@media (max-width: 1024px) {
			width: calc(50% - 10px);
		}

		@media (max-width: 600px) {
			width: 100% !important;
			float: none;
		}
	}

	.form-columns-2 .hs-input:nth-of-type(2) {
		width: calc(50% - 20px);
		float: right;

		@media (max-width: 600px) {
			width: 100% !important;
			float: none;
		}
	}

	.hs-input:nth-of-type(2) {
		width: 100%;
	}

	fieldset.form-columns-2 {
		display: flex;
		gap: 40px;

		@media (max-width: 1024px) {
			gap: 20px;
		}

		@media (max-width: 600px) {
			flex-wrap: wrap;

			.input {
				margin-right: 0 !important;
			}
		}
	}

	.hs-input[type='checkbox']:after {
		content: '';
		background: white;
		color: #7360f2;
		border: 1px solid #7360f2;
		border-radius: 7px;
		width: 24px;
		padding: 1px 11px;
	}

	.hs-input[type='checkbox']:checked::after {
		background: #7360f2;
		background-image: url(/images/blog/Vector.svg);
		background-repeat: no-repeat;
		background-size: contain;
	}

	.hs-input[type='radio']:after {
		content: '';
		background: white;
		color: #7360f2;
		border: 1px solid #7360f2;
		border-radius: 50%;
		width: 24px;
		min-width: 24px;
		padding: 0 11px;
	}

	.hs-input[type='radio']:checked::after {
		background-color: #7360f2;
		box-shadow: inset 0 0 0 4px #f4f4f4;
	}

	.hs-fieldtype-checkbox > .input > ul.inputs-list {
		display: flex;
		justify-content: space-between;
		flex-direction: column;
	}

	.hs-form-checkbox label {
		display: flex;
		align-items: center;

		span {
			margin-left: 13px;
		}
	}

	.submitted-message {
		font-family: var(--font-sans);

		font-size: 28px;
		line-height: 35px;
		text-align: center;
		color: #000000 !important;
	}
`;

export default HubspotForm;
